import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { setCurrentSpreadIndexAction, setFocusedContentAction } from 'editor/src/store/editor/slice';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import { moveSpreadContentAction } from '../../slice';
import { getSpreadContentMoves } from '../../util/moveSpreadContentUtil';

// Moves the artwork of one slot onto another. All three arguments are *spread*
// indexes, never the indexes of the list a drag reports: callers translate, so
// that a filtered or re-sorted list cannot be mistaken for the spreads array.
//
// `eligibleSpreadIndexes` is that list in the order it was shown, which decides
// both what may move and which slots shift out of the way.
const moveSpreadContentOperation =
  (fromSpreadIndex: number, toSpreadIndex: number, eligibleSpreadIndexes: number[]): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const { designData } = state.design;
    if (!designData) {
      return;
    }

    const moves = getSpreadContentMoves(designData, fromSpreadIndex, toSpreadIndex, eligibleSpreadIndexes);
    if (!moves.length) {
      return;
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation('spread reorder'));

      // Slots keep their position, so the editor would stay on one that now
      // holds different artwork. Follow the artwork instead.
      const { currentSpreadIndex, focusedContentAddress } = state.editor;
      const destinationOf = new Map(moves.map((move) => [move.fromSpreadIndex, move.toSpreadIndex]));

      const nextCurrentSpreadIndex = destinationOf.get(currentSpreadIndex);
      if (nextCurrentSpreadIndex !== undefined) {
        dispatch(setCurrentSpreadIndexAction(nextCurrentSpreadIndex));
      }

      if (focusedContentAddress) {
        const nextFocusedSpreadIndex = destinationOf.get(focusedContentAddress.spreadIndex);
        if (nextFocusedSpreadIndex !== undefined) {
          dispatch(setFocusedContentAction({ ...focusedContentAddress, spreadIndex: nextFocusedSpreadIndex }));
        }
      }

      dispatch(moveSpreadContentAction({ moves }));
    });
  };

export default moveSpreadContentOperation;
